import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { Matrix4, Euler, Vector3 } from 'three';
import { measureWalls } from './wall-thickness.js';
import { disconnectedDiagnosis } from './mold-diagnostics.js';
import { checkPullPaths } from './pull-check.js';
import { createRayQuery } from './ray-query.js';
import { bandGrooveCut } from './band-grooves.js';
import { directionalFill } from './model-adaptation.js';
import { threadPositions } from './funnel.js';

export const defaults = {
  height: 100,
  wall: 10,
  clearance: 0.15,
  fit: 0.25,
  split: 'auto',
  offset: 0,
  pour: 10,
  vent: 1.6,
  wick: 1.8,
  rotateX: 0,
  rotateY: 0,
  rotateZ: 0,
  pieces: 2,
  pourCount: 1,
  funnel: false,
  band: true,
  holes: [],
  planeAngle: 0,
  planeTilt: 0,
  minWall: 2,
  fillDetached: false,
  removeDetached: false,
  adaptForSplit: false,
  adaptMethod: 'directional',
};

export function validateSettings(input) {
  const settings = { ...defaults, ...input };
  const ranges = {
    height: [20, 250],
    wall: [2, 25],
    clearance: [0, 0.8],
    fit: [0.1, 0.6],
    offset: [-25, 25],
    pour: [4, 20],
    vent: [0.8, 3],
    wick: [0, 4],
    rotateX: [-180, 180],
    rotateY: [-180, 180],
    rotateZ: [-180, 180],
    planeAngle: [-180, 180],
    planeTilt: [-90, 90],
    minWall: [0.4, 10],
  };
  for (const [key, [min, max]] of Object.entries(ranges)) {
    if (!Number.isFinite(settings[key]) || settings[key] < min || settings[key] > max)
      throw Error(`${key} must be between ${min} and ${max}.`);
  }
  if (!['auto', 'x', 'y', 'z', 'custom'].includes(settings.split))
    throw Error('Choose a valid parting direction.');
  if (![2, 3, 4].includes(settings.pieces)) throw Error('Choose 2, 3 or 4 mold pieces.');
  if (![1, 2, 3, 4].includes(settings.pourCount)) throw Error('Choose 1–4 pour inlets.');
  if (typeof settings.funnel !== 'boolean') throw Error('Invalid funnel setting.');
  if (
    settings.funnel &&
    (settings.split === 'z' || (settings.split === 'custom' && settings.planeTilt !== 0))
  )
    throw Error(
      'The screw-in funnel needs a vertical split with a flat mold top. Choose X, Y or zero tilt.'
    );
  if (typeof settings.band !== 'boolean') throw Error('Invalid rubber-band groove setting.');
  if (!['directional', 'convex'].includes(settings.adaptMethod))
    throw Error('Invalid adaptation method.');
  if (typeof settings.adaptForSplit !== 'boolean') throw Error('Invalid model adaptation setting.');
  if (settings.adaptForSplit && settings.split === 'auto')
    throw Error('Choose an explicit split before adapting the model.');
  if (typeof settings.fillDetached !== 'boolean')
    throw Error('Invalid detached-region fill setting.');
  if (typeof settings.removeDetached !== 'boolean') throw Error('Invalid removeDetached setting.');
  if (
    !Array.isArray(settings.holes) ||
    settings.holes.length > settings.pourCount ||
    settings.holes.some(
      (p) =>
        !Array.isArray(p) || p.length !== 3 || p.some((n) => !Number.isFinite(n) || n < 0 || n > 1)
    )
  )
    throw Error('Invalid manual pour-hole positions.');
  if (settings.pieces > 2 && settings.offset !== 0)
    throw Error('Radial molds require a centered seam. Set seam offset to 0.');
  if (
    settings.pieces > 2 &&
    (settings.split === 'z' || (settings.split === 'custom' && settings.planeTilt !== 0))
  )
    throw Error('Radial molds use vertical planes. Choose X, Y or a custom plane with tilt 0.');
  return settings;
}

export function selectPourPoints(positions, bounds, count, diameter, manual = []) {
  const chosen = manual.map((p) =>
    p.map((n, i) => bounds.min[i] + n * (bounds.max[i] - bounds.min[i]))
  );
  const spacing = (diameter + 1) ** 2;
  const distance = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
  for (let i = 0; i < chosen.length; i++)
    for (let j = 0; j < i; j++)
      if (distance(chosen[i], chosen[j]) < spacing)
        throw Error('Pour inlets overlap. Move them apart or reduce the inlet diameter.');
  const candidates = [];
  for (let i = 0; i < positions.length; i += 3) {
    const p = Array.from(positions.subarray(i, i + 3));
    // Favor high surface points; auto channels remain vertical and may join below.
    if (p[2] >= bounds.max[2] - (bounds.max[2] - bounds.min[2]) * 0.25) candidates.push(p);
  }
  candidates.sort((a, b) => b[2] - a[2] || a[0] ** 2 + a[1] ** 2 - (b[0] ** 2 + b[1] ** 2));
  while (chosen.length < count) {
    const available = candidates.filter((p) => chosen.every((q) => distance(p, q) >= spacing));
    if (!available.length)
      throw Error(
        'There is not enough room for this many separate pour inlets. Reduce the count or diameter, or place them manually.'
      );
    let selected = available[0];
    if (chosen.length)
      selected = available.reduce((best, p) => {
        const score = (q) => Math.min(...chosen.map((c) => distance(q, c)));
        return score(p) > score(best) ? p : best;
      });
    chosen.push(selected);
  }
  return chosen;
}

export function detectPocketVents(query, bounds, pourPoints, { pour = 15, vent = 1.6 } = {}) {
  if (!query || !bounds) return [];
  const bb = bounds;
  const step = 2.0;
  const nx = Math.ceil((bb.max[0] - bb.min[0]) / step);
  const ny = Math.ceil((bb.max[1] - bb.min[1]) / step);
  if (nx <= 2 || ny <= 2) return [];

  const grid = Array.from({ length: nx }, () => new Float32Array(ny).fill(-Infinity));
  for (let i = 0; i < nx; i++) {
    const x = bb.min[0] + (i + 0.5) * step;
    for (let j = 0; j < ny; j++) {
      const y = bb.min[1] + (j + 0.5) * step;
      const hit = query.firstHit([x, y, bb.max[2] + 5], [x, y, bb.min[2] - 5]);
      if (hit) grid[i][j] = hit.position[2];
    }
  }

  const rawPeaks = [];
  for (let i = 1; i < nx - 1; i++) {
    for (let j = 1; j < ny - 1; j++) {
      const z = grid[i][j];
      if (z === -Infinity) continue;
      let isMax = true;
      let minLocal = Infinity;
      for (let di = -2; di <= 2; di++) {
        for (let dj = -2; dj <= 2; dj++) {
          if (di === 0 && dj === 0) continue;
          const ni = i + di;
          const nj = j + dj;
          if (ni < 0 || ni >= nx || nj < 0 || nj >= ny) continue;
          const nz = grid[ni][nj];
          if (Math.abs(di) <= 1 && Math.abs(dj) <= 1 && nz > z + 0.01) {
            isMax = false;
          }
          if (nz !== -Infinity && nz < minLocal) {
            minLocal = nz;
          }
        }
      }
      if (isMax && z - minLocal >= 1.0) {
        const x = bb.min[0] + (i + 0.5) * step;
        const y = bb.min[1] + (j + 0.5) * step;
        const overhead = query.firstHit([x, y, z + 0.2], [x, y, bb.max[2] + 10]);
        if (!overhead) {
          rawPeaks.push([+x.toFixed(2), +y.toFixed(2), +z.toFixed(2)]);
        }
      }
    }
  }

  rawPeaks.sort((a, b) => b[2] - a[2]);

  const clusterRadius = 8.0;
  const distinctPeaks = [];
  for (const p of rawPeaks) {
    if (!distinctPeaks.some((d) => Math.hypot(d[0] - p[0], d[1] - p[1]) < clusterRadius)) {
      distinctPeaks.push(p);
    }
  }

  const pourRadius = pour / 2 + 2.0;
  return distinctPeaks.filter((p) => {
    return !pourPoints.some((pourP) => Math.hypot(pourP[0] - p[0], pourP[1] - p[1]) < pourRadius);
  });
}

export function parseSTL(buffer) {
  if (buffer.byteLength > 20 * 1024 * 1024) throw Error('Use an STL smaller than 20 MB.');
  // Verify binary length before the STL loader allocates typed arrays.
  if (buffer.byteLength >= 84) {
    const count = new DataView(buffer).getUint32(80, true);
    if (
      84 + count * 50 !== buffer.byteLength &&
      !new TextDecoder().decode(buffer.slice(0, 80)).trimStart().startsWith('solid')
    )
      throw Error('Invalid binary STL length. Export the mesh again before uploading.');
  } else if (!new TextDecoder().decode(buffer).trimStart().startsWith('solid')) {
    throw Error('This file is too short to be a valid STL.');
  }
  let geometry;
  try {
    geometry = new STLLoader().parse(buffer);
  } catch {
    throw Error('This file could not be read as an STL.');
  }
  const vertices = geometry.attributes.position?.array;
  if (!vertices || vertices.length < 36 || !vertices.every(Number.isFinite))
    throw Error('Use a valid STL with at least 4 triangles.');
  return geometry;
}

export function solidFromPositions(wasm, positions) {
  let signed = 0;
  for (let i = 0; i < positions.length; i += 9) {
    const [ax, ay, az, bx, by, bz, cx, cy, cz] = positions.slice(i, i + 9);
    signed += ax * (by * cz - bz * cy) + ay * (bz * cx - bx * cz) + az * (bx * cy - by * cx);
  }
  positions = new Float32Array(positions);
  if (signed < 0)
    for (let i = 0; i < positions.length; i += 9)
      for (let k = 0; k < 3; k++)
        [positions[i + 3 + k], positions[i + 6 + k]] = [positions[i + 6 + k], positions[i + 3 + k]];
  const mesh = new wasm.Mesh({
    numProp: 3,
    vertProperties: positions,
    triVerts: Uint32Array.from({ length: positions.length / 3 }, (_, i) => i),
  });
  mesh.merge();
  try {
    const body = new wasm.Manifold(mesh);
    if (body.status() !== 'NoError' || body.isEmpty()) throw Error();
    return body;
  } catch {
    throw Error(
      'The STL must be one watertight solid. Repair holes, non-manifold edges and self-intersections before trying again.'
    );
  }
}

export function positionsOf(solid) {
  const mesh = solid.getMesh();
  const positions = new Float32Array(mesh.triVerts.length * 3);
  mesh.triVerts.forEach((vertex, i) => {
    positions.set(
      mesh.vertProperties.subarray(vertex * mesh.numProp, vertex * mesh.numProp + 3),
      i * 3
    );
  });
  return positions;
}

export function binarySTL(positions) {
  const count = positions.length / 9;
  const buffer = new ArrayBuffer(84 + count * 50);
  const view = new DataView(buffer);
  view.setUint32(80, count, true);
  const a = new Vector3(),
    b = new Vector3(),
    c = new Vector3();
  for (let i = 0; i < count; i++) {
    const offset = 84 + i * 50;
    a.fromArray(positions, i * 9);
    b.fromArray(positions, i * 9 + 3);
    c.fromArray(positions, i * 9 + 6);
    const normal = b.sub(a).cross(c.sub(a)).normalize();
    [normal.x, normal.y, normal.z].forEach((n, j) => view.setFloat32(offset + j * 4, n, true));
    for (let k = 0; k < 9; k++) view.setFloat32(offset + 12 + k * 4, positions[i * 9 + k], true);
  }
  return new Uint8Array(buffer);
}

export function releaseAnalysis(positions, bounds, axis, seam, query = null) {
  // Intersect a grid of axis-parallel rays with projected triangles.
  const [u, v] = axis === 0 ? [1, 2] : [0, 2];
  let sampled = 0,
    obstructed = 0;
  for (let row = 0; row < 24; row++)
    for (let col = 0; col < 24; col++) {
      const p = bounds.min[u] + ((bounds.max[u] - bounds.min[u]) * (row + 0.417)) / 24;
      const q = bounds.min[v] + ((bounds.max[v] - bounds.min[v]) * (col + 0.389)) / 24;
      const start = [...bounds.min],
        end = [...bounds.max];
      start[axis] -= 1;
      end[axis] += 1;
      start[u] = end[u] = p;
      start[v] = end[v] = q;
      const hits = query ? query.allHits(start, end).map((point) => point[axis]) : [];
      if (!query)
        for (let i = 0; i < positions.length; i += 9) {
          const au = positions[i + u],
            av = positions[i + v];
          const bu = positions[i + 3 + u] - au,
            bv = positions[i + 3 + v] - av;
          const cu = positions[i + 6 + u] - au,
            cv = positions[i + 6 + v] - av;
          const det = bu * cv - bv * cu;
          if (Math.abs(det) < 1e-9) continue;
          const s = ((p - au) * cv - (q - av) * cu) / det;
          const t = (bu * (q - av) - bv * (p - au)) / det;
          if (s >= 0 && t >= 0 && s + t <= 1)
            hits.push(
              positions[i + axis] +
                s * (positions[i + 3 + axis] - positions[i + axis]) +
                t * (positions[i + 6 + axis] - positions[i + axis])
            );
        }
      hits.sort((a, b) => a - b);
      const unique = hits.filter((x, i) => !i || x - hits[i - 1] > 1e-4);
      if (unique.length < 2) continue;
      sampled++;
      if (unique.length !== 2 || unique[0] > seam + 0.01 || unique.at(-1) < seam - 0.01)
        obstructed++;
    }
  return {
    sampled,
    obstructed,
    percent: Math.round((1000 * obstructed) / Math.max(1, sampled)) / 10,
  };
}

// Scoped to one scan. Never keep solids from another upload or WASM instance.
export function createGenerationCache() {
  return {
    source: null,
    owned: [],
    keep(solid) {
      this.owned.push(solid);
      return solid;
    },
    dispose() {
      this.owned.reverse().forEach((solid) => solid.delete());
      this.owned = [];
      this.source = null;
    },
  };
}

export function generate(
  wasm,
  buffer,
  input,
  progress = () => {},
  {
    preview = false,
    cache = null,
    exportWhen = null,
    diagnosticFeatures = true,
    removeDetached = false,
  } = {}
) {
  const s = validateSettings(input),
    M = wasm.Manifold;
  const owned = [];
  const keep = (value) => {
    owned.push(value);
    return value;
  };
  const cube = (low, high) => keep(keep(M.cube(high.map((n, i) => n - low[i]))).translate(low));
  const cyl = (r, h, pos, rotation = [0, 0, 0], r2 = r) =>
    keep(keep(keep(M.cylinder(h, r, r2, 48)).rotate(rotation)).translate(pos));
  const union = (list) => keep(M.union(list));
  const subtract = (a, b) => keep(a.subtract(b));
  try {
    progress('Checking the mesh', 8);
    const sourceKey = JSON.stringify([s.height, s.rotateX, s.rotateY, s.rotateZ]);
    if (
      cache?.source &&
      (cache.source.buffer !== buffer ||
        cache.source.wasm !== wasm ||
        cache.source.key !== sourceKey)
    )
      cache.dispose();
    let source = cache?.source;
    if (!source) {
      const geometry = parseSTL(buffer);
      geometry.applyMatrix4(
        new Matrix4().makeRotationFromEuler(
          new Euler(
            (s.rotateX * Math.PI) / 180,
            (s.rotateY * Math.PI) / 180,
            (s.rotateZ * Math.PI) / 180,
            'XYZ'
          )
        )
      );
      geometry.computeBoundingBox();
      const extent = geometry.boundingBox.getSize(new Vector3());
      if (Math.min(extent.x, extent.y, extent.z) < 1e-6)
        throw Error('The model must have thickness in every direction.');
      geometry.scale(s.height / extent.z, s.height / extent.z, s.height / extent.z);
      geometry.center();
      geometry.computeBoundingBox();
      const dimensions = geometry.boundingBox.getSize(new Vector3()).toArray();
      if (Math.max(...dimensions) > 300 || Math.min(...dimensions) < 2)
        throw Error('Scaled model dimensions must be 2–300 mm. Change height or orientation.');
      const body = solidFromPositions(wasm, geometry.attributes.position.array);
      cache ? cache.keep(body) : keep(body);
      const components = body.decompose();
      const componentCount = components.length;
      components.forEach((p) => p.delete());
      if (componentCount !== 1)
        throw Error(
          'Upload one connected solid. Separate shells and enclosed inner cavities are unsupported.'
        );
      source = {
        body,
        positions: positionsOf(body),
        query: createRayQuery(body),
        bounds: body.boundingBox(),
        dimensions,
        buffer,
        wasm,
        key: sourceKey,
        analyses: new Map(),
        cavities: new Map(),
        adaptations: new Map(),
      };
      geometry.dispose();
      if (cache) cache.source = source;
    }
    let body = source.body;
    const pos = source.positions,
      bounds = source.bounds,
      dimensions = source.dimensions;
    progress('Comparing parting directions', 20);
    let pourPoints = selectPourPoints(pos, bounds, s.pourCount, s.pour, s.holes);
    let channelPositions = pos,
      channelBounds = bounds,
      query = source.query;
    if (!source.analyses.has(s.offset)) {
      const directions = {};
      for (const [axis, name] of ['x', 'y'].entries())
        directions[name] = releaseAnalysis(
          pos,
          bounds,
          axis,
          (dimensions[axis] * s.offset) / 100,
          source.query
        );
      source.analyses.set(s.offset, directions);
    }
    const analyses = { ...source.analyses.get(s.offset) };
    const axis =
      s.split === 'auto' ? (analyses.x.percent <= analyses.y.percent ? 'x' : 'y') : s.split;
    const yaw = axis === 'y' ? 90 : axis === 'custom' ? s.planeAngle : 0;
    const tilt = axis === 'z' ? 90 : axis === 'custom' ? s.planeTilt : 0;
    const toWork = (shape) => keep(keep(shape.rotate([0, 0, -yaw])).rotate([0, tilt, 0]));
    const toWorld = (shape) => keep(keep(shape.rotate([0, -tilt, 0])).rotate([0, 0, yaw]));
    const inverse = new Matrix4()
      .makeRotationZ((yaw * Math.PI) / 180)
      .multiply(new Matrix4().makeRotationY((-tilt * Math.PI) / 180));
    const worldPoint = (p) => new Vector3().fromArray(p).applyMatrix4(inverse).toArray();
    const normal = worldPoint([1, 0, 0]);
    body = toWork(body);
    const modelPositions = pos;
    let bb = body.boundingBox();
    const seam = ((bb.max[0] - bb.min[0]) * s.offset) / 100;
    let adaptation = null;
    const adaptationRegions = [];
    if (s.adaptForSplit) {
      progress('Filling recesses for your selected split', 25);
      const originalBody = body;
      const adaptationKey = JSON.stringify([yaw, tilt, s.adaptMethod, s.pieces, s.offset]);
      let modified = source.adaptations.get(adaptationKey);
      if (!modified) {
        if (s.adaptMethod === 'convex') {
          const operands = [body];
          if (s.pieces === 2) {
            const mesh = body.getMesh();
            for (let i = 0; i < mesh.vertProperties.length; i += mesh.numProp)
              operands.push([seam, mesh.vertProperties[i + 1], mesh.vertProperties[i + 2]]);
          } else operands.push([0, 0, bb.min[2]], [0, 0, bb.max[2]]);
          modified = { solid: M.hull(operands), toleranceMm: 0 };
        } else modified = directionalFill(wasm, body, { pieces: s.pieces, seam });
        cache ? cache.keep(modified.solid) : keep(modified.solid);
        source.adaptations.set(adaptationKey, modified);
      }
      body = modified.solid;
      if (body.status() !== 'NoError' || body.isEmpty())
        throw Error('The model adjustment could not produce a valid solid.');
      const added = keep(body.subtract(originalBody));
      const addedVolume = Math.max(0, added.volume());
      if (addedVolume > 0)
        adaptationRegions.push({
          positions: positionsOf(toWorld(added)),
          volumeMm3: addedVolume,
          kind: 'directional_fill',
        });
      adaptation = {
        applied: true,
        method: s.adaptMethod === 'convex' ? 'convex_envelope' : 'directional_fill',
        addedCandleVolumeMm3: addedVolume,
        toleranceMm: modified.toleranceMm,
        partQuantizationMm: 0.00001,
        partSimplificationMm: 0.0001,
        pieces: s.pieces,
        split: axis,
        planeAngle: yaw,
        planeTilt: tilt,
        seamOffsetMm: seam,
        limitations:
          'A local core fill is tried first. This directional fill preserves the source and fills toward the selected parting surfaces; it is an approximate construction, not a globally minimal redesign. Review added volume in amber.',
      };
      bb = body.boundingBox();
      const adjustedWorld = toWorld(body);
      query = createRayQuery(adjustedWorld);
      channelPositions = positionsOf(adjustedWorld);
      channelBounds = adjustedWorld.boundingBox();
      adaptation.adjustedDimensionsMm = channelBounds.max.map((n, i) => n - channelBounds.min[i]);
      const manual = s.holes.map((point) =>
        point.map(
          (n, i) =>
            (bounds.min[i] + n * (bounds.max[i] - bounds.min[i]) - channelBounds.min[i]) /
            (channelBounds.max[i] - channelBounds.min[i])
        )
      );
      let pourCandidates = channelPositions;
      if (s.adaptMethod === 'convex') {
        const top = [];
        for (let i = 0; i < channelPositions.length; i += 3)
          if (channelPositions[i + 2] >= channelBounds.max[2] - 0.001)
            top.push(Array.from(channelPositions.subarray(i, i + 3)));
        const center = [0, 1, 2].map((i) => top.reduce((sum, p) => sum + p[i], 0) / top.length);
        pourCandidates = new Float32Array([...center, ...channelPositions]);
        adaptation.autoChannelsRelocated = true;
      }
      pourPoints = selectPourPoints(pourCandidates, channelBounds, s.pourCount, s.pour, manual);
      for (const [i, name] of ['x', 'y'].entries())
        analyses[name] = releaseAnalysis(
          positionsOf(adjustedWorld),
          adjustedWorld.boundingBox(),
          i,
          0,
          query
        );
    }
    if (axis === 'custom' || axis === 'z')
      analyses[axis] = releaseAnalysis(positionsOf(body), bb, 0, seam, createRayQuery(body));
    let cavity = body;
    let expansionMax = 0;
    if (s.clearance) {
      progress('Adding cavity clearance', 35);
      const cavityKey = JSON.stringify([
        s.clearance,
        yaw,
        tilt,
        ...(s.adaptForSplit ? [s.pieces, s.offset, s.adaptMethod] : []),
      ]);
      let expanded = source.cavities.get(cavityKey);
      if (!expanded) {
        // Octahedral geodesic sphere, inflated to contain a true clearance sphere.
        const sphere = keep(M.sphere(1, 8));
        const spherePositions = positionsOf(sphere);
        let inradius = 1;
        for (let i = 0; i < spherePositions.length; i += 9) {
          const a = new Vector3().fromArray(spherePositions, i);
          const b = new Vector3().fromArray(spherePositions, i + 3).sub(a);
          const c = new Vector3().fromArray(spherePositions, i + 6).sub(a);
          inradius = Math.min(inradius, Math.abs(a.dot(b.cross(c).normalize())));
        }
        expansionMax = s.clearance / inradius;
        const shape = body.minkowskiSum(keep(sphere.scale(expansionMax)));
        cache ? cache.keep(shape) : keep(shape);
        expanded = { shape, expansionMax };
        source.cavities.set(cavityKey, expanded);
      }
      expansionMax = expanded.expansionMax;
      cavity = expanded.shape;
    }
    progress('Building shells and connections', 62);
    // Fixed-size pins and sockets need a rim independent of the shell setting.
    const funnelRim = s.funnel ? s.pour / 2 + 5 + s.fit : 0;
    const hardwareRim = Math.max(8, s.wall, funnelRim);
    const margins = [Math.max(s.wall, funnelRim), hardwareRim, hardwareRim];
    const low = bb.min.map((n, i) => n - margins[i] - expansionMax);
    const high = bb.max.map((n, i) => n + margins[i] + expansionMax);
    // A narrow candle or offset seam can otherwise leave a socket open at the back.
    if (s.pieces === 2) high[0] = Math.max(high[0], seam + 4 + s.fit + s.wall);
    const corners = [];
    for (const x of [low[0], high[0]])
      for (const y of [low[1], high[1]])
        for (const z of [low[2], high[2]]) corners.push(worldPoint([x, y, z]));
    const worldLow = [0, 1, 2].map((i) => Math.min(...corners.map((p) => p[i])));
    const worldHigh = [0, 1, 2].map((i) => Math.max(...corners.map((p) => p[i])));
    const worldCyl = (r, h, p) => toWork(cyl(r, h, p));
    let parts = [];
    const radius =
      Math.hypot(
        Math.max(Math.abs(bb.min[0]), Math.abs(bb.max[0])),
        Math.max(Math.abs(bb.min[1]), Math.abs(bb.max[1]))
      ) +
      hardwareRim +
      expansionMax;
    const pins = [],
      sockets = [],
      cuts = [cavity];
    const featureGroups = {
      'pour inlets': [],
      'air vents': [],
      'wick outlet': [],
      'band grooves': [],
      'alignment key holes': [],
      'registration sockets': [],
    };
    const cut = (name, shape) => {
      cuts.push(shape);
      featureGroups[name].push(shape);
    };
    if (s.pieces === 2)
      for (const y of [low[1] + hardwareRim / 2, high[1] - hardwareRim / 2]) {
        for (const z of [low[2] + hardwareRim / 2, high[2] - hardwareRim / 2]) {
          pins.push(cyl(2.5, 4.5, [seam - 0.5, y, z], [0, 90, 0], 2));
          sockets.push(cyl(2.5 + s.fit, 4 + s.fit + 0.1, [seam - 0.1, y, z], [0, 90, 0]));
        }
      }
    const points = [];
    for (let i = 0; i < channelPositions.length; i += 3)
      points.push(Array.from(channelPositions.subarray(i, i + 3)));
    const bottom = points.reduce((a, b) => (b[2] < a[2] ? b : a));
    for (const p of pourPoints)
      cut('pour inlets', worldCyl(s.pour / 2, worldHigh[2] - p[2] + 3, [p[0], p[1], p[2] - 2]));
    let funnelThread = null,
      funnelSocket = null,
      funnelInfo = null;
    if (s.funnel) {
      const root = s.pour / 2 + 2;
      const p = pourPoints[0];
      const seatZ = worldHigh[2];
      const local = new Vector3().fromArray(p).applyMatrix4(inverse.clone().invert());
      const socketMargin = root + 1 + s.fit + 2;
      const room =
        s.pieces === 2
          ? Math.min(local.x - low[0], high[0] - local.x, local.y - low[1], high[1] - local.y)
          : radius - Math.hypot(local.x, local.y);
      if (room + 1e-6 < socketMargin)
        throw Error(
          'The funnel socket needs more material around inlet 1. Move it inward or increase the mold wall.'
        );
      if (
        pourPoints
          .slice(1)
          .some(
            (other) =>
              Math.hypot(other[0] - p[0], other[1] - p[1]) < root + 1 + s.fit + s.pour / 2 + 1
          )
      )
        throw Error('The funnel socket overlaps another inlet. Move the inlets farther apart.');
      funnelThread = keep(solidFromPositions(wasm, threadPositions(root)));
      funnelSocket = keep(solidFromPositions(wasm, threadPositions(root, s.fit)));
      cut('pour inlets', toWork(keep(funnelSocket.translate([p[0], p[1], seatZ - 6]))));
      // Open the female thread at the top and give the nozzle an entry lead-in.
      cut('pour inlets', worldCyl(root + 1 + s.fit, 1, [p[0], p[1], seatZ - 0.5]));
      funnelInfo = {
        inlet: 1,
        seat: [p[0], p[1], seatZ],
        pitchMm: 3,
        engagementMm: 6,
        boreDiameterMm: s.pour,
        mouthDiameterMm: 44,
        reservoirMl: +(
          (((Math.PI * 30) / 3) * ((s.pour / 2) ** 2 + (s.pour / 2) * 22 + 22 ** 2)) /
          1000
        ).toFixed(1),
        radialFitMm: s.fit,
      };
    }
    const band = points.filter(
      (p) => p[2] > channelBounds.max[2] - (channelBounds.max[2] - channelBounds.min[2]) * 0.15
    );
    const ventPoints = [];
    for (const sign of [-1, 1]) {
      let p = band.reduce((a, b) => (sign * b[1] > sign * a[1] ? b : a));
      if (s.adaptForSplit && s.adaptMethod === 'convex')
        p = p.map((n, i) => (n + 2 * pourPoints[0][i]) / 3);
      ventPoints.push(p);
    }
    const pocketVents = detectPocketVents(query, channelBounds, pourPoints, s);
    for (const p of pocketVents) {
      if (!ventPoints.some((v) => Math.hypot(v[0] - p[0], v[1] - p[1]) < 8)) {
        ventPoints.push(p);
      }
    }
    for (const p of ventPoints) {
      cut('air vents', worldCyl(s.vent / 2, worldHigh[2] - p[2] + 2, [p[0], p[1], p[2] - 1]));
    }
    if (s.wick)
      cut(
        'wick outlet',
        worldCyl(s.wick / 2, bottom[2] - worldLow[2] + 3, [bottom[0], bottom[1], worldLow[2] - 1])
      );
    const bandLevels = [low[2] + (high[2] - low[2]) * 0.25, low[2] + (high[2] - low[2]) * 0.75];
    if (s.band)
      for (const z of bandLevels)
        cut('band grooves', keep(bandGrooveCut(wasm, { low, high, radius, pieces: s.pieces, z })));
    let keys = null,
      collars = null,
      singleCollar = null;
    if (s.pieces > 2) {
      const keyParts = [];
      for (let i = 0; i < s.pieces; i++) {
        const theta = (i * 2 * Math.PI) / s.pieces;
        const p = [
          (radius - hardwareRim / 2) * Math.cos(theta),
          (radius - hardwareRim / 2) * Math.sin(theta),
          low[2] + 2,
        ];
        cut('alignment key holes', cyl(2.5 + s.fit, high[2] - low[2] - 1, p));
        const keyLength = high[2] - low[2] + 1;
        keyParts.push(
          union([
            cyl(2.5, keyLength - 2, [i * 9, 0, 0]),
            cyl(2.5, 1, [i * 9, 0, keyLength - 3], [0, 0, 0], 3.5),
            cyl(3.5, 2, [i * 9, 0, keyLength - 2]),
          ])
        );
      }
      keys = union(keyParts);
      // Separate sliding collars close radial sections without cyclic fixed pins.
      const collar = subtract(
        cyl(radius + s.fit + 4, 8, [0, 0, 0]),
        cyl(radius + s.fit, 10, [0, 0, -1])
      );
      singleCollar = collar;
      collars = union([collar, keep(collar.translate([2 * (radius + s.fit + 4) + 5, 0, 0]))]);
    }
    const allCuts = union(cuts);
    const bases = [];
    if (s.pieces === 2) {
      bases.push(
        union([cube(low, [seam, high[1], high[2]]), ...pins]),
        cube([seam, low[1], low[2]], high)
      );
      parts = [subtract(bases[0], allCuts), subtract(bases[1], union([allCuts, ...sockets]))];
    } else {
      const blank = cyl(radius, high[2] - low[2], [0, 0, low[2]]);
      for (let i = 0; i < s.pieces; i++) {
        const a = (i * 2 * Math.PI) / s.pieces,
          b = ((i + 1) * 2 * Math.PI) / s.pieces;
        const wedge = keep(
          keep(blank.trimByPlane([-Math.sin(a), Math.cos(a), 0], 0)).trimByPlane(
            [Math.sin(b), -Math.cos(b), 0],
            0
          )
        );
        bases.push(wedge);
        parts.push(subtract(wedge, allCuts));
      }
    }
    if (s.adaptForSplit)
      parts = parts.map((part) => {
        const vertices = positionsOf(part);
        for (let i = 0; i < vertices.length; i++)
          vertices[i] = Math.round(vertices[i] * 100000) / 100000;
        return keep(keep(solidFromPositions(wasm, vertices)).simplify(0.0001));
      });
    progress('Validating the printable parts', 85);
    for (const part of parts) {
      if (part.status() !== 'NoError' || part.isEmpty() || part.volume() <= 0)
        throw Error(
          'This configuration did not produce valid solids. Try another split or thicker walls.'
        );
    }
    const fillCandidates = [];
    let filledRegions = [...adaptationRegions];
    let fillAttempt = null;
    let repair = {
      requested: s.fillDetached,
      applied: false,
      regionCount: 0,
      addedCandleVolumeMm3: 0,
    };
    const diagnosticContext = {
      analyzeFeatures: diagnosticFeatures,
      parts,
      bases,
      groups: featureGroups,
      sockets,
      toWorld,
      positionsOf,
      keep,
      union,
      subtract,
      model: modelPositions,
      onFillCandidate: (candidate) => fillCandidates.push(candidate),
      touchesExterior: (component) => {
        const mesh = component.getMesh();
        for (let i = 0; i < mesh.vertProperties.length; i += mesh.numProp) {
          const [x, y, z] = mesh.vertProperties.subarray(i, i + 3);
          if (Math.abs(z - low[2]) < 0.02 || Math.abs(z - high[2]) < 0.02) return true;
          if (s.pieces === 2) {
            if (
              Math.abs(x - low[0]) < 0.02 ||
              Math.abs(x - high[0]) < 0.02 ||
              Math.abs(y - low[1]) < 0.02 ||
              Math.abs(y - high[1]) < 0.02
            )
              return true;
          } else if (Math.hypot(x, y) >= radius * Math.cos(Math.PI / 48) - 0.02) return true;
        }
        return false;
      },
      report: {
        settings: s,
        funnel: funnelInfo,
        pieces: s.pieces,
        splitAxis: axis,
        splitPlane: { normal, offsetMm: seam, angle: yaw, tilt },
        pullDirections:
          s.pieces === 2
            ? [normal.map((n) => -n), normal]
            : Array.from({ length: s.pieces }, (_, i) =>
                worldPoint([
                  Math.cos(((i + 0.5) * 2 * Math.PI) / s.pieces),
                  Math.sin(((i + 0.5) * 2 * Math.PI) / s.pieces),
                  0,
                ])
              ),
        displayLift: s.pieces === 2 ? -worldLow[2] : -low[2],
        modelDimensions: dimensions,
        moldDimensions:
          s.pieces === 2
            ? worldHigh.map((n, i) => n - worldLow[i])
            : [radius * 2, radius * 2, high[2] - low[2]],
      },
    };
    let diagnostic = disconnectedDiagnosis({ ...diagnosticContext, parts, cavity });
    if (diagnostic && s.fillDetached) {
      const accepted = [],
        reasons = [];
      for (const candidate of fillCandidates) {
        if (candidate.reachesExterior) {
          reasons.push(
            `Part ${candidate.part}: filling this region would open the mold exterior and risk wax leakage. Change the split or remove the separating feature.`
          );
          continue;
        }
        if (!candidate.verified) {
          const trial = union([cavity, candidate.solid]);
          const boundaries = trial.decompose().map(keep);
          if (
            trial.status() !== 'NoError' ||
            boundaries.filter((p) => p.volume() > 0).length !== 1
          ) {
            reasons.push(
              `Part ${candidate.part}: the proposed fill is not connected to the candle cavity. It would create an isolated pocket that wax cannot reach. Change the split or the separating feature.`
            );
            continue;
          }
        }
        accepted.push(candidate);
      }
      fillCandidates.splice(0, fillCandidates.length, ...accepted);
      fillAttempt = {
        requested: true,
        status: accepted.length ? 'pending' : 'blocked',
        reasons: reasons.length
          ? reasons
          : accepted.length
            ? []
            : ['No positive-volume detached material could be verified for filling.'],
      };
    }
    if (diagnostic && s.fillDetached && fillCandidates.length) {
      progress('Filling selected candle recesses', 87);
      filledRegions = [
        ...adaptationRegions,
        ...diagnostic.fragments.filter((f) =>
          fillCandidates.some((c) => c.part === f.part && c.component === f.component)
        ),
      ];
      const additions = fillCandidates.map((c) => c.solid);
      repair = {
        requested: true,
        applied: true,
        regionCount: additions.length,
        addedCandleVolumeMm3: +additions.reduce((sum, part) => sum + part.volume(), 0).toFixed(4),
        regions: filledRegions
          .filter((f) => f.kind !== 'directional_fill')
          .map(({ positions, ...info }) => info),
      };
      parts = parts.map((part, index) => {
        const remove = fillCandidates.filter((c) => c.part === index + 1).map((c) => c.solid);
        return remove.length ? subtract(part, union(remove)) : part;
      });
      cavity = keep(union([cavity, ...additions]).simplify(0.0001));
      repair.cavitySimplificationMm = 0.0001;
      for (const part of [...parts, cavity])
        if (part.status() !== 'NoError' || part.isEmpty() || part.volume() <= 0)
          throw Error(
            'Filling could not produce valid solids. Undo the fill and change the split plane.'
          );
      diagnostic = disconnectedDiagnosis({
        ...diagnosticContext,
        parts,
        cavity,
        onFillCandidate: () => {},
      });
      if (fillAttempt) fillAttempt.status = diagnostic ? 'partial' : 'applied';
      if (diagnostic) {
        diagnostic.report.repair = repair;
        diagnostic.filledRegions = filledRegions;
      }
      const filledBody = toWorld(cavity);
      const filledWorld = positionsOf(filledBody),
        filledQuery = createRayQuery(filledBody);
      for (const [i, name] of ['x', 'y'].entries())
        analyses[name] = releaseAnalysis(
          filledWorld,
          toWorld(cavity).boundingBox(),
          i,
          (dimensions[i] * s.offset) / 100,
          filledQuery
        );
      if (axis === 'z' || axis === 'custom')
        analyses[axis] = releaseAnalysis(
          positionsOf(cavity),
          cavity.boundingBox(),
          0,
          seam,
          createRayQuery(cavity)
        );
    }
    const removedDetached = [];
    if (diagnostic) {
      if (s.removeDetached || removeDetached) {
        progress('Removing detached mold walls', 88);
        parts = parts.map((part, index) => {
          const boundaries = part.decompose();
          const components = boundaries.filter((c) => c.volume() >= 0);
          const voids = boundaries.filter((c) => c.volume() < 0);
          voids.forEach((c) => c.delete());
          if (components.length <= 1) {
            components.forEach((c) => c.delete());
            return part;
          }
          components.sort((a, b) => b.volume() - a.volume());
          const mainPart = keep(components[0]);
          for (let i = 1; i < components.length; i++) {
            const vol = components[i].volume();
            removedDetached.push({ part: index + 1, volumeMm3: +vol.toFixed(4) });
            components[i].delete();
          }
          return mainPart;
        });
        diagnostic = null;
      } else {
        if (fillAttempt) diagnostic.report.fillAttempt = fillAttempt;
        const error = new Error(
          `Part ${diagnostic.report.diagnosis.affectedParts[0].part} contains detached mold regions. Inspect the red highlighted material and suggested changes.`
        );
        error.diagnostic = diagnostic;
        throw error;
      }
    }
    progress('Measuring minimum cavity walls', 90);
    const isSeam = (p) =>
      s.pieces === 2
        ? Math.abs(p[0] - seam) < 0.02
        : Array.from({ length: s.pieces }, (_, i) => (i * 2 * Math.PI) / s.pieces).some(
            (a) => Math.abs(-Math.sin(a) * p[0] + Math.cos(a) * p[1]) < 0.02
          );
    const rayQueries = parts.map(createRayQuery);
    const wallThickness = measureWalls(parts, cavity, isSeam, s.minWall, rayQueries);
    progress('Checking each section’s pull path', 93);
    const pullCheck = checkPullPaths(
      parts,
      repair.applied ? cavity : body,
      s.pieces === 2
        ? [
            [-1, 0, 0],
            [1, 0, 0],
          ]
        : Array.from({ length: s.pieces }, (_, i) => [
            Math.cos(((i + 0.5) * 2 * Math.PI) / s.pieces),
            Math.sin(((i + 0.5) * 2 * Math.PI) / s.pieces),
            0,
          ]),
      rayQueries
    );
    wallThickness.parts.forEach((reading) => {
      if (reading.location) {
        reading.location.start = worldPoint(reading.location.start);
        reading.location.end = worldPoint(reading.location.end);
      }
    });
    const assembled = parts.map((part) => positionsOf(toWorld(part)));
    const accessories = [];
    let funnelPrintable = null,
      funnelCoupon = null;
    if (s.funnel) {
      const root = s.pour / 2 + 2;
      const outer = union([
        funnelThread,
        cyl(root + 1, 1.2, [0, 0, 6]),
        cyl(root + 1, 30, [0, 0, 6], [0, 0, 0], 24),
      ]);
      const inside = union([
        cyl(s.pour / 2, 7.2, [0, 0, -0.1]),
        cyl(s.pour / 2, 30.2, [0, 0, 6], [0, 0, 0], 22.12),
      ]);
      const funnel = subtract(outer, inside);
      funnelPrintable = keep(keep(funnel.rotate([180, 0, 0])).translate([0, 0, 36]));
      funnelCoupon = subtract(
        cyl(root + s.fit + 4, 8, [0, 0, 0]),
        union([
          keep(funnelSocket.translate([0, 0, 2])),
          cyl(s.pour / 2, 8.2, [0, 0, -0.1]),
          cyl(root + 1 + s.fit, 0.6, [0, 0, 7.5]),
        ])
      );
      accessories.push({
        name: 'Screw-in funnel',
        positions: positionsOf(
          keep(funnel.translate([funnelInfo.seat[0], funnelInfo.seat[1], funnelInfo.seat[2] - 6]))
        ),
        slideDirection: [0, 0, 1],
      });
    }
    const moldDimensions =
      s.pieces === 2
        ? worldHigh.map((n, i) => n - worldLow[i])
        : [2 * radius, 2 * radius, high[2] - low[2]];
    const warnings = [
      ...(s.wall < hardwareRim
        ? [
            s.pieces === 2
              ? `Outer back walls use at least ${s.wall} mm; side and end rims use ${hardwareRim} mm to enclose pins and sockets.`
              : `Radial and end margins use ${hardwareRim} mm to enclose the fixed-size alignment keys.`,
          ]
        : []),
      ...(removedDetached.length
        ? [
            `Automatically removed ${removedDetached.length} detached mold wall(s) (${removedDetached.reduce((sum, r) => sum + r.volumeMm3, 0).toFixed(2)} mm³ total) from the mold sections to ensure single, printable solid halves.`,
          ]
        : []),
      ...(adaptation
        ? [
            `Candle model changed for the selected split: ${adaptation.method}, ${adaptation.addedCandleVolumeMm3.toFixed(3)} mm³ added. Review amber areas and candle-adapted.stl; model.stl preserves the original shape. Convex envelopes can remove substantial concave detail.`,
          ]
        : []),
      ...(repair.applied
        ? [
            `Candle shape changed: ${repair.regionCount} detached cavity core(s) were removed from the mold, filling ${repair.addedCandleVolumeMm3.toFixed(2)} mm³ of candle recesses. Amber areas show the added candle volume. Review candle-filled.stl before printing. Original model.stl is preserved; cavity clearance is included in the filled candle form.`,
          ]
        : []),
      ...(wallThickness.thin
        ? [
            `Thin cavity wall detected: sampled minimum ${wallThickness.minimumMm} mm is below your ${s.minWall} mm warning threshold. Inspect the highlighted location and increase wall thickness or move channels/hardware.`,
          ]
        : []),
      wallThickness.limitations,
      pullCheck.limitations,
      ...(!pullCheck.passed
        ? [
            'Sampled pull paths intersect the candle. These sections may lock around the shape; change the plane or use more sections.',
          ]
        : []),
      ...(tilt
        ? [
            'This mold uses a horizontal or tilted split. Pour and vent channels remain vertical, but band grooves and hardware follow the split frame. Verify assembly, sealing, support and release for this orientation.',
          ]
        : []),
      ...(analyses[axis].percent
        ? [
            `Potential undercuts or trapped regions on ${analyses[axis].percent}% of sampled pull lines. A rigid two-part mold may lock the candle. Try another orientation or use flexible tooling.`,
          ]
        : []),
      'Sampled release checks are not proof of demoldability. Fine hooks, tunnels and internal air traps can be missed. Inspect every cavity.',
      ...(s.pieces > 2
        ? [
            'Radial pull paths are checked by sampling, not a release guarantee. Inspect every section, including fine features between samples. Remove sliding collars and vertical keys before opening.',
          ]
        : []),
      ...(pocketVents.length
        ? [
            `Automatic air vent(s) added at ${pocketVents.length} local cavity high point(s) (e.g. tail or elevated features) to prevent trapped air pockets. Total vents: ${ventPoints.length}.`,
          ]
        : [
            `Two automatic top vents are included. Complex shapes may need extra vents at local high points. Inspect the inlet and vent connections. Manually placed vertical inlets can cut through other features; inspect their full path.`,
          ]),
      'Print the fit coupon first. Use material rated for your wax temperature; printed molds may soften or leak. Test a small pour with suitable release agent.',
      s.pieces === 2
        ? 'Seal the seam and wick outlet. Secure the halves with rubber bands or reusable straps. Pins align the halves; bands or straps hold them closed.'
        : 'Insert removable 5 mm alignment keys along each vertical seam. Slide one retaining collar onto each end and secure around the body with wide rubber bands or reusable straps. Verify closure before pouring. Keys align; collars and bands hold the sections closed.',
      ...(s.band
        ? [
            'Two 3 mm wide × 1.5 mm deep V-shaped rubber-band grooves with 45° flanks are cut around the exterior at 25% and 75% height. Select bands rated for the temperature and inspect their tension.',
          ]
        : []),
      ...(s.funnel
        ? [
            'Inlet 1 has a screw-in wax reservoir. Print the threaded fit coupon first. Keep vents clear; a frozen inlet stops shrinkage feeding. See FUNNEL.txt for top-up and demolding instructions.',
          ]
        : []),
      'Check slicer overhangs and bed size. Supports in detailed cavities may damage the surface. No wax shrinkage compensation is applied.',
    ];
    const report = {
      settings: s,
      funnel: funnelInfo,
      adaptation,
      repair,
      fillAttempt,
      removedDetached,
      splitAxis: axis,
      splitPlane: { normal, offsetMm: seam, angle: yaw, tilt },
      pullDirections:
        s.pieces === 2
          ? [normal.map((n) => -n), normal]
          : Array.from({ length: s.pieces }, (_, i) =>
              worldPoint([
                Math.cos(((i + 0.5) * 2 * Math.PI) / s.pieces),
                Math.sin(((i + 0.5) * 2 * Math.PI) / s.pieces),
                0,
              ])
            ),
      displayLift: s.pieces === 2 ? -worldLow[2] : -low[2],
      wallThickness,
      pullCheck,
      releaseAnalysis: analyses,
      sourceModelDimensions: dimensions.map((n) => +n.toFixed(2)),
      modelDimensions: (adaptation?.adjustedDimensionsMm || dimensions).map((n) => +n.toFixed(2)),
      moldDimensions: moldDimensions.map((n) => +n.toFixed(2)),
      triangles: pos.length / 9,
      volumeMl: +((repair.applied ? cavity.volume() : body.volume()) / 1000).toFixed(1),
      sourceVolumeMl: +(source.body.volume() / 1000).toFixed(1),
      clearanceRange: [s.clearance, +expansionMax.toFixed(4)],
      pieces: s.pieces,
      pourPoints,
      ventPoints,
      bandLevels: s.band ? bandLevels : [],
      bandGroove: s.band ? { profile: 'v', widthMm: 3, depthMm: 1.5, flankAngleDegrees: 45 } : null,
      enclosure: s.pieces === 2 ? 'rectangular' : 'cylindrical',
      hardwareRimMm: hardwareRim,
      pinDiameter: 5,
      pinLength: s.pieces === 2 ? 4 : high[2] - low[2] + 1,
      keyProtrusion: s.pieces === 2 ? 0 : 3,
      socketDiameter: 5 + 2 * s.fit,
      warnings,
      releaseRisk:
        analyses[axis].percent || s.pieces > 2 || repair.applied ? 'review' : 'low_sampled_risk',
    };
    if (preview || (exportWhen && !exportWhen(report)))
      return { report, assembled, accessories, model: modelPositions, filledRegions };
    const printPositions = parts.map((part, i) => {
      // Radial sections print flat on their bottom; split halves print back-side down.
      const rotated = s.pieces === 2 ? keep(part.rotate([0, i ? 90 : -90, 0])) : part;
      return positionsOf(keep(rotated.translate([0, 0, -rotated.boundingBox().min[2]])));
    });
    const couponA = union([
      cube([0, 0, 0], [16, 16, 4]),
      cyl(2.5, 4.5, [8, 8, 3.5], [0, 0, 0], s.pieces === 2 ? 2 : 2.5),
    ]);
    const couponB = subtract(
      cube([22, 0, 0], [38, 16, 6]),
      cyl(2.5 + s.fit, 4 + s.fit, [30, 8, 2 - s.fit])
    );
    const files = {
      'fit-coupon.stl': binarySTL(positionsOf(union([couponA, couponB]))),
      'model.stl': binarySTL(modelPositions),
    };
    if (s.funnel) {
      files['funnel.stl'] = binarySTL(positionsOf(funnelPrintable));
      files['funnel-thread-coupon.stl'] = binarySTL(positionsOf(funnelCoupon));
      files['funnel-assembled.stl'] = binarySTL(accessories[0].positions);
    }
    if (adaptation?.applied) files['candle-adapted.stl'] = binarySTL(positionsOf(toWorld(body)));
    if (repair.applied) {
      // Quantize to STL's float32 coordinates before simplifying sub-micron seams.
      const quantized = keep(solidFromPositions(wasm, positionsOf(toWorld(cavity))));
      const stable = keep(quantized.simplify(0.0001));
      files['candle-filled.stl'] = binarySTL(positionsOf(stable));
      files['filled-regions.stl'] = binarySTL(
        new Float32Array(filledRegions.flatMap((f) => Array.from(f.positions)))
      );
    }
    parts.forEach((_, i) => {
      const name = s.pieces === 2 ? (i ? 'right' : 'left') : String(i + 1);
      files[`mold-${name}.stl`] = binarySTL(printPositions[i]);
      files[`${name}-assembled.stl`] = binarySTL(assembled[i]);
    });
    if (keys) files['alignment-keys.stl'] = binarySTL(positionsOf(keys));
    if (collars) files['retaining-collars.stl'] = binarySTL(positionsOf(collars));
    if (singleCollar) files['retaining-collar-print-2.stl'] = binarySTL(positionsOf(singleCollar));
    progress('Packing your mold kit', 96);
    return { report, files, assembled, accessories, model: modelPositions, filledRegions };
  } finally {
    // A fresh worker per run also releases the entire WASM heap on completion/cancel.
    owned.reverse().forEach((part) => {
      try {
        part.delete();
      } catch {
        /* already disposed */
      }
    });
  }
}
